import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { hostname, getSupportContactInfo } from "../../utils";
import { useWhitelabel } from "../../context/WhitelabelContext";

interface WhitelabelInstance {
  id: string;
  domain?: string;
  settings: any;
  is_owner: boolean;
  created_date?: string;
  updated_date?: string;
  domain_provisioned?: boolean;
  domain_status?: string;
  domain_provider?: string;
  domain_error?: string;
}

interface DomainStatus {
  domain: string | null;
  status: string;
  provider: string;
  provisioned?: boolean;
  last_checked?: string;
  message?: string;
  error?: string;
  instructions?: string;
  dns_configured?: string;
}

export default function DNSSetupDialog({
  open,
  onClose,
  whitelabel,
  getToken,
}: {
  open: boolean;
  onClose: () => void;
  whitelabel: WhitelabelInstance | null;
  getToken: Function;
}) {
  const { whitelabelSettings } = useWhitelabel();
  const [copiedText, setCopiedText] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [domainStatus, setDomainStatus] = useState<DomainStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  // For the IP address from API response
  const [targetIPAddress, setTargetIPAddress] =
    useState<string>("149.248.204.158"); // fallback IP address

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    setSnackbarOpen(true);
  };

  const checkDomainStatus = async () => {
    if (!whitelabel || !whitelabel.id || !whitelabel.domain) return;

    setCheckingStatus(true);
    try {
      const currentToken = await getToken();

      // First trigger a refresh
      await axios.post(
        `${hostname}/whitelabel/${whitelabel.id}/domain/refresh`,
        {},
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      // Then get the status
      const response = await axios.get(
        `${hostname}/whitelabel/${whitelabel.id}/domain/status`,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );
      setDomainStatus(response.data);
    } catch (error) {
      console.error("Error checking domain status:", error);
    } finally {
      setCheckingStatus(false);
    }
  };

  useEffect(() => {
    if (open && whitelabel && whitelabel.domain) {
      checkDomainStatus();
    } else {
      setDomainStatus(null);
    }
  }, [open, whitelabel]);

  // Effect to update IP address from API response
  useEffect(() => {
    if (domainStatus?.instructions) {
      // Extract IP address from instructions (ex: "Configure an A record for your domain to point to 149.248.204.158")
      const ipMatch = domainStatus.instructions.match(
        /\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/
      );
      if (ipMatch && ipMatch[0]) {
        setTargetIPAddress(ipMatch[0]);
      }
    }
  }, [domainStatus]);

  if (!whitelabel) {
    return null;
  }

  // Extract subdomain and root domain
  const domainParts = whitelabel?.domain ? whitelabel.domain.split(".") : [];
  const isSubdomain = domainParts.length > 2;
  const recordName = isSubdomain ? domainParts[0] : "@";

  // For backward compatibility, determine if using legacy provisioning
  const isLegacyProvisioning = domainStatus?.provider === "netlify";

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          DNS Setup {whitelabel.domain ? `for ${whitelabel.domain}` : ""}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {!whitelabel.domain ? (
              <Alert severity="warning">
                Please configure a domain first before setting up DNS.
              </Alert>
            ) : (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Alert
                    severity="info"
                    icon={<InfoOutlinedIcon />}
                    sx={{ flexGrow: 1 }}
                  >
                    To use your custom domain, you need to configure your DNS
                    settings to point to {whitelabelSettings?.title || "GTM AI"}'s servers.
                  </Alert>
                  <Tooltip title="Refresh domain status">
                    <IconButton
                      onClick={checkDomainStatus}
                      disabled={checkingStatus}
                      sx={{ ml: 1 }}
                    >
                      {checkingStatus ? (
                        <CircularProgress size={24} />
                      ) : (
                        <SyncIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Domain Status */}
                {domainStatus && (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography variant="h6">Domain Status</Typography>
                      {domainStatus?.status === "active" && (
                        <Chip
                          icon={<AutoFixHighIcon />}
                          label="Active"
                          color="success"
                          size="small"
                        />
                      )}
                    </Box>
                    <Divider sx={{ mb: 2 }} />

                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Chip label="Domain" size="small" />
                        <Typography variant="body2">
                          {domainStatus.domain}
                        </Typography>
                      </Box>

                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Chip
                          label="Status"
                          size="small"
                          color={
                            domainStatus.status === "active"
                              ? "success"
                              : domainStatus.status === "pending"
                              ? "success"
                              : domainStatus.status === "failed" ||
                                domainStatus.status === "error"
                              ? "error"
                              : "default"
                          }
                        />
                        <Typography variant="body2">
                          {domainStatus.status === "active"
                            ? "Active and Ready"
                            : domainStatus.status === "pending"
                            ? "Active and Ready"
                            : domainStatus.status === "pending_configuration"
                            ? "Waiting for DNS Configuration"
                            : domainStatus.status === "failed" ||
                              domainStatus.status === "error"
                            ? "Configuration Failed"
                            : domainStatus.status === "manual"
                            ? "Manual DNS Configuration Required"
                            : domainStatus.status}
                        </Typography>
                      </Box>

                      {domainStatus.message && (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Chip label="Message" size="small" />
                          <Typography variant="body2">
                            {domainStatus.message}
                          </Typography>
                        </Box>
                      )}

                      {domainStatus.dns_configured !== undefined && (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Chip
                            label="DNS Check"
                            size="small"
                            color={
                              domainStatus.dns_configured
                                ? "success"
                                : "warning"
                            }
                          />
                          <Typography variant="body2">
                            {domainStatus.dns_configured
                              ? "DNS is properly configured"
                              : "DNS is not yet configured properly"}
                          </Typography>
                        </Box>
                      )}

                      {domainStatus.last_checked && (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Chip label="Last Checked" size="small" />
                          <Typography variant="body2">
                            {new Date(
                              domainStatus.last_checked
                            ).toLocaleString()}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                )}

                {domainStatus?.status === "active" ? (
                  <>
                    <Alert severity="info" icon={<InfoOutlinedIcon />}>
                      <Typography variant="body2" paragraph>
                        <strong>
                          Your domain has been provisioned with {whitelabelSettings?.title || "GTM AI"}.
                        </strong>{" "}
                        Your DNS settings are correctly pointing to our servers
                        and your whitelabel is working.
                      </Typography>
                      <Typography variant="body2">
                        Your SSL certificate has been provisioned, enabling
                        secure HTTPS access to your custom domain. If you
                        experience any issues, please contact support at {getSupportContactInfo(whitelabelSettings)}.
                      </Typography>
                    </Alert>

                    <Alert severity="success" icon={<CheckCircleOutlineIcon />}>
                      <Typography variant="body2">
                        Your whitelabel instance is now accessible at{" "}
                        <strong>
                          <Link
                            href={`https://${whitelabel.domain}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {whitelabel.domain}
                          </Link>
                        </strong>
                      </Typography>
                    </Alert>
                  </>
                ) : (
                  <>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Step 1: Required DNS Setup
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                        sx={{ mb: 1 }}
                      >
                        To connect your custom domain to {whitelabelSettings?.title || "GTM AI"}, please add
                        the following A record in your domain registrar's DNS
                        settings. This record will point your domain to our
                        servers:
                      </Typography>

                      <Paper variant="outlined" sx={{ p: 2, mt: 1, mb: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          A Record
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mt: 1,
                          }}
                        >
                          <Chip label="Type" size="small" />
                          <Typography variant="body2">A</Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mt: 1,
                          }}
                        >
                          <Chip label="Name" size="small" />
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace" }}
                          >
                            {recordName}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleCopy(recordName, "Record name")
                            }
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mt: 1,
                          }}
                        >
                          <Chip label="Target IP" size="small" />
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace" }}
                          >
                            {targetIPAddress}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleCopy(targetIPAddress, "Target IP")
                            }
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mt: 1,
                          }}
                        >
                          <Chip label="TTL" size="small" />
                          <Typography variant="body2">
                            300 (or your provider's default)
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            mt: 2,
                            pt: 1,
                            borderTop: "1px dashed rgba(0, 0, 0, 0.1)",
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            <strong>How to add this record:</strong> Log in to
                            your domain registrar (like GoDaddy, Namecheap, or
                            Cloudflare), go to DNS settings, and add a new A
                            record with the values above. This will direct all
                            traffic from your domain to {whitelabelSettings?.title || "GTM AI"}'s servers.
                          </Typography>
                        </Box>
                      </Paper>

                      <Alert severity="info">
                        <Typography variant="body2" paragraph>
                          <strong>1. Create A Record:</strong> The A record
                          above tells DNS servers to point requests for your
                          domain to {whitelabelSettings?.title || "GTM AI"}'s server at IP address{" "}
                          {targetIPAddress}. Make sure to use the exact Name and
                          Target IP values shown above.
                        </Typography>
                        <Typography variant="body2" paragraph>
                          <strong>2. DNS Propagation:</strong> After updating
                          your DNS settings, it can take up to 24-48 hours for
                          changes to propagate across the internet, though it's
                          often much faster (minutes to hours). During this
                          time, your domain may not yet connect to {whitelabelSettings?.title || "GTM AI"}.
                        </Typography>
                        <Typography variant="body2">
                          <strong>3. SSL Certificate:</strong> After DNS
                          verification completes, {whitelabelSettings?.title || "GTM AI"} automatically
                          provisions an SSL certificate for your custom domain.
                          This enables HTTPS (secure) access to your whitelabel
                          instance. The SSL certificate is typically issued
                          within 10-30 minutes after DNS propagation.
                        </Typography>
                      </Alert>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: 2,
                        mt: 3,
                        mb: 1,
                      }}
                    >
                      <Button
                        variant="contained"
                        startIcon={<SyncIcon />}
                        onClick={checkDomainStatus}
                        disabled={checkingStatus}
                      >
                        {checkingStatus
                          ? "Checking & Refreshing..."
                          : "Refresh & Check Status"}
                      </Button>
                    </Box>
                    <Typography
                      variant="body2"
                      align="center"
                      color="text.secondary"
                      sx={{ mt: 1, mb: 3 }}
                    >
                      After you've added the DNS records, click the button above
                      to refresh and check your setup. This will trigger
                      {whitelabelSettings?.title || "GTM AI"} to verify your DNS configuration. It may take
                      several attempts as DNS changes can take time to
                      propagate.
                    </Typography>

                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Step 2: Verify DNS Propagation
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        DNS changes can take up to 24-48 hours to propagate
                        worldwide, though it's often much faster.
                      </Typography>
                      <List>
                        <ListItem>
                          <ListItemText
                            primary="Check DNS propagation"
                            secondary={`You can use tools like whatsmydns.net to check if your DNS changes have propagated`}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Test your domain"
                            secondary={`Once propagated, you should be able to access ${whitelabelSettings?.title || "GTM AI"} at ${whitelabel.domain}`}
                          />
                        </ListItem>
                      </List>
                    </Box>

                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Common DNS Providers
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Here's how to add DNS records with popular providers:
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemText
                            primary="Cloudflare"
                            secondary="DNS → Records → Add Record → Type: A"
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="GoDaddy"
                            secondary="DNS → Manage Zones → Add → Type: A"
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Namecheap"
                            secondary="Advanced DNS → Add New Record → Type: A"
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Google Domains"
                            secondary="DNS → Manage Custom Records → Create New Record → Type: A"
                          />
                        </ListItem>
                      </List>
                    </Box>

                    <Alert severity="warning">
                      <Typography variant="body2">
                        <strong>Important:</strong> Make sure to remove any
                        existing A, AAAA, or CNAME records for the same hostname
                        to avoid conflicts. Only one A record should point to
                        the Approximated IP address.
                      </Typography>
                    </Alert>

                    <Alert severity="success" icon={<CheckCircleOutlineIcon />}>
                      <Typography variant="body2">
                        Once your DNS is configured and propagated, your
                        whitelabel instance will be accessible at{" "}
                        <strong>{whitelabel.domain}</strong>
                      </Typography>
                    </Alert>
                  </>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message={`${copiedText} copied to clipboard`}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  );
}
